import { type NotificationPreset, NotificationPresetSchema } from '@developerz/domain';
import { For, type JSX } from 'solid-js';
import { t } from '../../i18n';
import type { PresetEstimates } from '../../lib/notification-estimate';
import { PRESET_DESC_KEY, PRESET_LABEL_KEY } from '../../lib/notification-labels';
import { Panel } from '../Panel';
import { Button } from '../ui/Button';
import { NotificationPresetEstimate } from './NotificationPresetEstimate';

// Three starting points. Each button is `aria-pressed` from the SERVER's
// `preset` (the preset the switches currently equal, or null), so a manual
// change un-presses all three and the line under them says "Custom". One click
// is one request; the container owns it.
//
// Slice 21 (#5706): each button also carries what the last week would have
// been under that preset ("about 2 a week"), when the estimate has been
// measured. `undefined` numbers print nothing — an estimate with no history
// behind it must not paint three placeholder metrics.

export function NotificationPresets(props: {
  readonly preset: NotificationPreset | null;
  readonly onPick: (preset: NotificationPreset) => void;
  /** Per-preset weekly numbers, or undefined when there is nothing to print. */
  readonly estimates?: PresetEstimates | undefined;
  /** The estimate's read hit its row cap: every number is a floor. */
  readonly floor?: boolean | undefined;
}): JSX.Element {
  return (
    <Panel title={t('notifications_settings.preset.heading')} class="notification-presets">
      <p class="card__desc">{t('notifications_settings.preset.hint')}</p>
      <div class="notification-presets__row">
        <For each={NotificationPresetSchema.options}>
          {(preset) => (
            <Button
              variant="outline"
              aria-pressed={props.preset === preset ? 'true' : 'false'}
              onClick={() => props.onPick(preset)}
            >
              {t(PRESET_LABEL_KEY[preset])}
              {/* The space is load-bearing: without it the button's accessible
                  name is "Only urgentabout 2 a week". */}{' '}
              <NotificationPresetEstimate
                estimate={props.estimates?.[preset]}
                floor={props.floor}
              />
            </Button>
          )}
        </For>
      </div>
      <p class="notification-presets__current" aria-live="polite">
        {t(PRESET_DESC_KEY[props.preset ?? 'custom'])}
      </p>
    </Panel>
  );
}
